import { is, forEach, prop, propEq, omit } from 'ramda';
import {
  disablePersistenceAndClearStateFromStorage,
  enablePersistence,
  persistStore,
} from '../utils/statePersistenceControl';

import states from './states';
import {
  EXTENSION_DURATION,
  SET_REGISTRATION_VALUES,
  SET_RENTAL_DRIVER_DETAILS,
  SET_VEHICLE_INFORMATION_DETAILS,
} from '../ducks/rentalMission';

export const UPDATE_STATE = '@@machine/UPDATE_STATE';

const getBuilder = prop('exec');
const isActionsBuilder = propEq('type', '$state');
const getActions = (action, state) => getBuilder(action)(state, action);

/**
 * Creates a Redux middleware that integrates with an XState state machine.
 *
 * The middleware intercepts actions and, if they are valid events for the state machine,
 * transitions the machine and handles the resulting state and actions.
 *
 * @param {Object} machine - The XState machine to integrate with Redux
 * @returns {Function} Redux middleware function that:
 *   1. Passes all actions to the next middleware
 *   2. If the action is a valid state machine event, transitions the machine
 *   3. Updates the Redux state with the new machine state
 *   4. Executes or dispatches any resulting actions from the transition
 *
 * @example
 * const stateMachine = createMachine({...});
 * const store = createStore(
 *   rootReducer,
 *   applyMiddleware(createMiddleware(stateMachine))
 * );
 */
export const createMiddleware = machine => {
  const validActions = machine.events;
  return ({ dispatch, getState }) => next => event => {
    const res = next(event);
    if (validActions.includes(event.type)) {
      const state = getState();
      const nextStateMachine = machine.transition(state.xstate.value, event, omit(['xstate'], state));

      const callfn = fnOrObject => {
        const fn = is(Function, fnOrObject) ? { type: fnOrObject.name, exec: fnOrObject } : fnOrObject;
        if (!is(Function, fn.exec)) throw new Error(`FSM middleware: '${fn.type}' is not an action`);
        dispatch(fn.exec(event));
      };

      dispatch({ type: UPDATE_STATE, payload: nextStateMachine });
      forEach(action => {
        if (isActionsBuilder(action)) {
          forEach(callfn, getActions(action, state));
        } else {
          callfn(action);
        }
      }, nextStateMachine.actions);
    }
    return res;
  };
};

const PERSISTENCE_ACTIONS = [
  UPDATE_STATE,
  SET_REGISTRATION_VALUES,
  SET_RENTAL_DRIVER_DETAILS,
  SET_VEHICLE_INFORMATION_DETAILS,
  EXTENSION_DURATION,
];

const ENTRY_STATES = [
  states.RENTAL_CAR_WELCOME,
  states.BENEFIT_SELECTION,
  states.RENTAL_EXTENSION_VEHICLE_LOCATION,
  states.RENTAL_EXTENSION_NOT_ELIGIBLE,
];

const EXIT_STATES = [states.RENTAL_SUMMARY, states.PREVIOUS_CLAIM_HANDLING, states.CALL_ME_BACK, states.VOICE_EXIT];

/**
 * Middleware for handling persistence logic in a state machine-driven Redux store.
 *
 * This middleware intercepts actions, determines the next state of the state machine,
 * and manages persistence based on entry/exit states and specific actions.
 *
 * @param {Object} machine - The state machine instance with a `transition` method.
 * @returns {Function} Redux middleware function.
 *
 * @param {Object} store - The Redux store.
 * @param {Function} next - The next middleware or reducer in the chain.
 * @param {Object} action - The dispatched Redux action.
 * @returns {*} The result of calling `next(action)`.
 *
 * Side Effects:
 * - Enables or disables persistence based on state transitions.
 * - Persists state when certain actions are dispatched.
 */
export const persistenceMiddleware = machine => store => next => action => {
  const result = next(action);

  const state = store.getState();
  const nextStateMachine = machine.transition(state.xstate.value, action, omit(['xstate'], state));

  const session = state?.global?.session;

  if (ENTRY_STATES.includes(nextStateMachine.value)) {
    enablePersistence(session);
  } else if (EXIT_STATES.includes(nextStateMachine.value)) {
    disablePersistenceAndClearStateFromStorage(session);
  }

  if (PERSISTENCE_ACTIONS.includes(action.type)) {
    persistStore(session, state, nextStateMachine);
  }

  return result;
};

export const createReducer = (initialState = {}) => (state = initialState, { type, payload }) =>
  type === UPDATE_STATE ? payload : state;
